import React, { useState } from 'react';
import '../assets/css/layout/faq.css';

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const faqCategories = [
        {
            category: "Getting Started",
            questions: [
                {
                    question: "What is E-AquaHealth?",
                    answer: "E-AquaHealth is a comprehensive digital platform designed for modern fish farmers. We provide access to veterinary consultations, laboratory services, an aquaculture marketplace, educational resources, and farm management tools - all in one place."
                },
                {
                    question: "How do I create an account?",
                    answer: "Click on 'Register' on our homepage. You can sign up as a Fish Farmer, Veterinarian, Laboratory, or Vendor. Fill in your details, verify your email, and you're ready to go. Professional accounts (Vet, Lab, Vendor) require additional verification."
                },
                {
                    question: "Is E-AquaHealth free to use?",
                    answer: "Creating an account and browsing our platform is free. Some services, such as veterinary consultations, laboratory tests, and marketplace purchases, have associated fees. Educational courses may be free or paid depending on the content."
                },
                {
                    question: "What types of accounts are available?",
                    answer: "We offer four account types: Fish Farmer (for aquaculture professionals), Veterinarian (for licensed aquatic vets), Laboratory (for certified testing facilities), and Vendor (for aquaculture product suppliers). Each has different features and requirements."
                }
            ]
        },
        {
            category: "Veterinary Services",
            questions: [
                {
                    question: "How do I consult with a veterinarian?",
                    answer: "Log into your account, navigate to 'AquaDoc' or 'Doctors', browse available veterinarians, and request a consultation. You can describe your issue, upload photos, and the vet will respond with advice and treatment recommendations."
                },
                {
                    question: "Are the veterinarians licensed?",
                    answer: "Yes, all veterinarians on our platform are verified and licensed professionals specializing in aquatic animal health. We verify credentials before approving veterinarian accounts."
                },
                {
                    question: "How quickly will I get a response from a vet?",
                    answer: "Response times vary based on the veterinarian's availability. Most consultations receive an initial response within 24-48 hours. For urgent cases, we recommend marking your case as high priority."
                },
                {
                    question: "Can I get prescriptions through E-AquaHealth?",
                    answer: "Veterinarians can provide treatment recommendations and prescriptions where legally permitted. Some medications may require in-person examination. Your vet will advise on the appropriate course of action."
                }
            ]
        },
        {
            category: "Laboratory Services",
            questions: [
                {
                    question: "What types of tests do laboratories offer?",
                    answer: "Our partner laboratories offer water quality testing, disease diagnosis, pathogen identification, feed analysis, and more. Available tests vary by laboratory. You can view each lab's service menu on their profile."
                },
                {
                    question: "How do I submit samples for testing?",
                    answer: "Create a case, select a laboratory, and follow their sample submission guidelines. Most labs will provide collection instructions, shipping requirements, and sample preservation protocols."
                },
                {
                    question: "How long do test results take?",
                    answer: "Turnaround time depends on the type of test and the laboratory. Simple water quality tests may be completed within 24-48 hours, while complex pathology tests may take 5-7 business days."
                },
                {
                    question: "Are the laboratories certified?",
                    answer: "Yes, all laboratories on our platform are verified and hold appropriate certifications for aquaculture diagnostics. We review certifications and accreditations before approving laboratory accounts."
                }
            ]
        },
        {
            category: "Marketplace",
            questions: [
                {
                    question: "What products are available in the marketplace?",
                    answer: "Our marketplace offers a wide range of aquaculture products including fish feed, water treatment chemicals, pond equipment, health products, testing kits, and more from verified vendors."
                },
                {
                    question: "How do I know if a vendor is trustworthy?",
                    answer: "All vendors are verified before joining our platform. You can view vendor ratings, reviews, and verification badges. We also offer buyer protection for purchases made through our marketplace."
                },
                {
                    question: "What payment methods are accepted?",
                    answer: "We accept various payment methods including credit/debit cards, mobile money, and bank transfers. Available options may vary by region."
                },
                {
                    question: "What is the return policy?",
                    answer: "Return policies vary by vendor and product type. Generally, you can request a return within 7 days of receiving your order. Check the product page for specific return terms."
                }
            ]
        },
        {
            category: "Learning & Courses",
            questions: [
                {
                    question: "What courses are available?",
                    answer: "We offer courses on various aquaculture topics including fish health management, water quality, feeding practices, disease prevention, business management, and more. New courses are added regularly."
                },
                {
                    question: "Do I get a certificate after completing a course?",
                    answer: "Yes, upon successful completion of a course, you will receive a digital certificate that you can download and share. Certificates include your name, course title, and completion date."
                },
                {
                    question: "Are the courses self-paced?",
                    answer: "Most of our courses are self-paced, allowing you to learn at your convenience. Some specialized courses may have scheduled live sessions or deadlines."
                },
                {
                    question: "Can I access courses on mobile?",
                    answer: "Yes, all courses are accessible on desktop, tablet, and mobile devices. Our platform is fully responsive for learning on the go."
                }
            ]
        },
        {
            category: "Account & Security",
            questions: [
                {
                    question: "How do I reset my password?",
                    answer: "Click 'Forgot Password' on the login page, enter your registered email, and we'll send you a password reset link. The link expires after 24 hours for security."
                },
                {
                    question: "How is my data protected?",
                    answer: "We use industry-standard encryption (SSL/TLS) for all data transmission. Your personal and business information is stored securely and never shared with third parties without your consent."
                },
                {
                    question: "Can I delete my account?",
                    answer: "Yes, you can request account deletion by contacting our support team. Please note that some data may be retained as required by law or for legitimate business purposes."
                },
                {
                    question: "How do I update my profile information?",
                    answer: "Log into your account, go to 'Profile' or 'Settings', and update your information. Some changes (like email) may require verification."
                }
            ]
        }
    ];

    let globalIndex = 0;

    return (
        <div className="faq-container">
            <div className="faq-header">
                <h1>Frequently Asked Questions</h1>
                <p>Find answers to common questions about E-AquaHealth</p>
            </div>

            <div className="faq-content">
                {faqCategories.map((category, catIndex) => (
                    <div key={catIndex} className="faq-category">
                        <h2 className="category-title">{category.category}</h2>
                        <div className="faq-list">
                            {category.questions.map((faq, qIndex) => {
                                const currentIndex = globalIndex++;
                                return (
                                    <div 
                                        key={qIndex} 
                                        className={`faq-item ${openIndex === currentIndex ? 'open' : ''}`}
                                    >
                                        <button 
                                            className="faq-question"
                                            onClick={() => toggleFAQ(currentIndex)}
                                            aria-expanded={openIndex === currentIndex}
                                        >
                                            <span>{faq.question}</span>
                                            <span className="faq-icon">
                                                {openIndex === currentIndex ? '−' : '+'}
                                            </span>
                                        </button>
                                        <div className="faq-answer">
                                            <p>{faq.answer}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="faq-contact">
                <h3>Still have questions?</h3>
                <p>Can&apos;t find the answer you&apos;re looking for? Our support team is here to help.</p>
                <a href="/contact" className="contact-btn">Contact Support</a>
            </div>
        </div>
    );
};

export default FAQ;
